import { Component, OnDestroy, OnInit } from '@angular/core';
import { BaseCustomEditFieldComponent } from '../../../../base/form';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { get_studio_fields_info } from '../../../../shared/utils/studio';
import { arr_first, blank, data_get, data_nullify, observe, data_is_true, isId, OptionalValue } from '../../../../shared/utils/common';
import { debounceTime, distinctUntilChanged, startWith, switchMap } from 'rxjs/operators';
import { ApiService } from '../../../../services/api.service';
import { AssetService } from '../../../../services/asset.service';

@Component({
  selector: 'asset-variant-input',
  template: `
    <ng-select
      [(ngModel)]="value"
      [loading]="fetching$ | async"
      [typeahead]="typeahead$"
      [items]="items$ | async"
      appendTo="body"
      bindLabel="text"
      bindValue="id"
      (ngModelChange)="onChange()"
    >
      <ng-template ng-label-tmp let-item="item">
        {{ item.text | translate }}
      </ng-template>

      <ng-template
        ng-option-tmp
        let-item="item"
      >
        <fieldmagic-indent
          *ngIf="! item.is_parent"
        >
          <span>{{ item.text | translate }}</span>
        </fieldmagic-indent>

        <span *ngIf="item.is_parent">{{ item.text | translate }}</span>
      </ng-template>
    </ng-select>
  `
})
export class AssetVariantInputComponent extends BaseCustomEditFieldComponent implements OnInit, OnDestroy {
  value?: OptionalValue<string>;

  readonly fetching$ = new BehaviorSubject<boolean>(false);
  readonly typeahead$ = new Subject<string>();

  items$: Observable<AssetTypeVariantOption[]>;

  private readonly assetTypeId$ = new BehaviorSubject<OptionalValue<string>>(null);

  private _onAssetTypeFieldChangeSubscription?: Subscription;

  constructor(
    private readonly _api: ApiService,
    private readonly _assetService: AssetService,
  ) {
    super();
  }

  ngOnInit(): void {
    this._listenToAssetTypeFieldChange();

    this.value = data_nullify(data_get(this.field$.value, 'default_value'));

    if (data_is_true(data_get(this.data$.value, 'in_manage_assets') && isId(data_get(this.data$.value, 'asset_type_id')))) {
      this.assetTypeId$.next(data_get(this.data$.value, 'asset_type_id'));
      this.value = data_get(this.parent$.value, 'value.asset_type_variant_id');
    }

    this.items$ = combineLatest([
      this.typeahead$.pipe(
        startWith(''),
        distinctUntilChanged(),
      ),
      this.assetTypeId$,
    ]).pipe(
      debounceTime(300),
      switchMap(([term, assetTypeId]) => observe({
        before: () => this.fetching$.next(true),
        after: () => this.fetching$.next(false),
        observable: () => this._api.callPrivate$({
          action: 'post',
          path: '/asset_types/search_variants',
          expectsNewFormat: true,
          body: {
            term: data_nullify(term),
            id: this.value,
            asset_type_id: assetTypeId,
          },
          onSuccessResponse: (response) => response.get('items'),
        }),
      })),
    );

    this.parent$.value.patchValue({
      'asset_type_variant_id': this.value,
    });
  }

  ngOnDestroy(): void {
    if (this._onAssetTypeFieldChangeSubscription) {
      this._onAssetTypeFieldChangeSubscription.unsubscribe();
    }
  }

  onChange(): void {
    this.parent$.value.patchValue({
      'asset_type_variant_id': this.value,
    });

    this._assetService.variantInputChanged(this.value);
  }

  private _listenToAssetTypeFieldChange(): void {
    const metadata = arr_first(get_studio_fields_info(this.tabs$.value, {
      field_names: ['asset_type_id'],
    }));

    if (blank(metadata)) {
      return;
    }

    this._onAssetTypeFieldChangeSubscription = metadata.form_control.valueChanges
      .pipe(
        startWith(metadata.field.default_value),
        distinctUntilChanged(),
      )
      .subscribe((value) => {
        this.assetTypeId$.next(value);
        this.value = null;
        this.parent$.value.patchValue({
          'asset_type_variant_id': null,
        });
      });
  }
}

type AssetTypeVariantOption = {
  id: string;
  text: string;
  is_parent: boolean;
}